
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Converts a hex color code to HSL color values
 * @param hex Hex color code (e.g. #FF0000)
 * @returns HSL color object or null if invalid
 */
export function hexToHSL(hex: string): { h: number; s: number; l: number } | null {
  // Remove the '#' if present
  hex = hex.replace('#', '');
  
  // Validate hex format
  if (!/^[0-9A-Fa-f]{6}$/.test(hex)) {
    return null;
  }
  
  // Parse the hex color
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    
    h = Math.round(h * 60);
  }
  
  s = Math.round(s * 100);
  l = Math.round(l * 100);
  
  return { h, s, l };
}

/**
 * Adjusts the brightness of a color
 * @param hex Hex color code
 * @param percent Percentage to adjust (-100 to 100)
 * @returns Adjusted hex color
 */
export function adjustColorBrightness(hex: string, percent: number): string {
  // Remove the '#' if present
  hex = hex.replace('#', '');
  
  // Parse the hex color
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Adjust brightness
  const adjustR = Math.max(0, Math.min(255, Math.round(r + (percent / 100) * 255)));
  const adjustG = Math.max(0, Math.min(255, Math.round(g + (percent / 100) * 255)));
  const adjustB = Math.max(0, Math.min(255, Math.round(b + (percent / 100) * 255)));
  
  // Convert back to hex
  return `#${adjustR.toString(16).padStart(2, '0')}${adjustG.toString(16).padStart(2, '0')}${adjustB.toString(16).padStart(2, '0')}`;
}

/**
 * Determines if text should be black or white based on background color
 * Uses improved YIQ formula for better contrast calculation
 * @param hexColor Hex color code
 * @returns #000000 for black or #ffffff for white
 */
export function getContrastColor(hexColor: string): string {
  // Remove the '#' if present
  const hex = hexColor.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Calculate relative luminance using the sRGB color space formula
  // This provides better contrast calculation than simple YIQ
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Return black for light colors and white for dark colors
  // Using 0.55 threshold for better results with more colors
  return luminance > 0.55 ? '#000000' : '#ffffff';
}
